import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import axios from "axios";
import { useNavigate } from "react-router-dom";

import LayoutWrapper from "../../../components/LayoutWrapper";
import HeaderBar from "../../../components/HeaderBar";

import {
  FaPlus,
  FaSearch,
  FaFilter,
  FaArrowLeft,
  FaCog,
  FaInfoCircle,
  FaGasPump,
  FaCar,
  FaTimes,
  FaClock,
} from "react-icons/fa";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import { ThemeContext } from "../../../context/ThemeContext";
import getCurrentSkin from "../../../utils/getCurrentSkin";

/* =========================================================
   API
========================================================= */

const BASE =
  "https://nissi-india.com/api/hsdhr";

/* =========================================================
   HELPERS
========================================================= */

// Local date (not UTC) — toISOString() gives yesterday before 05:30 IST
const toInputDate = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;
const showVal = (value) =>
  value === null ||
  value === undefined ||
  value === ""
    ? "—"
    : value;

/* =========================================================
   DEFAULT DATE HELPERS
========================================================= */

const getDefaultFromDate = () => {
  const date =
    new Date();

  date.setMonth(
    date.getMonth() - 6
  );

  return toInputDate(
    date
  );
};

const getTodayDate = () =>
  toInputDate(
    new Date()
  );

/* =========================================================
   COMPONENT
========================================================= */

const HsdHrUpdate = () => {
  const navigate =
    useNavigate();

  /* =======================================================
     THEME
  ======================================================= */

  const {
    isDark,
    skin,
    setSkin,
    theme,
  } = useContext(
    ThemeContext
  );

  const currentSkin =
    useMemo(
      () =>
        getCurrentSkin(
          theme || skin
        ),
      [theme, skin]
    );

  const baseColor =
    currentSkin?.baseColor ||
    "blue";

  const baseShade =
    currentSkin?.baseShade ||
    "600";

  /* =======================================================
     LOCAL STORAGE
  ======================================================= */

  const finYearId =
    localStorage.getItem(
      "finYearId"
    ) || "";

  const finYear =
    localStorage.getItem(
      "finYear"
    ) || "";

  const storedBranchId =
    localStorage.getItem(
      "branchId"
    ) ||
    localStorage.getItem(
      "branchid"
    ) ||
    "";

  const branch =
    localStorage.getItem(
      "branchType"
    ) ||
    localStorage.getItem(
      "branchtype"
    ) ||
    localStorage.getItem(
      "branch"
    ) ||
    "";

  const userId =
    localStorage.getItem(
      "userId"
    ) ||
    localStorage.getItem(
      "loginuserid"
    ) ||
    "";

  /* =======================================================
     HEADERS
  ======================================================= */

  const commonHeaders =
    useMemo(
      () => ({
        "x-finyearid":
          finYearId,

        "x-finyear":
          finYear,

        "x-branchid":
          storedBranchId,

        "x-branch":
          branch,

        "x-userid":
          userId,
      }),
      [
        finYearId,
        finYear,
        storedBranchId,
        branch,
        userId,
      ]
    );

  /* =======================================================
     DESIGN CLASSES
  ======================================================= */

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark
      ? "text-slate-300"
      : "text-slate-700"
  }`;

  const cardClass = `rounded-2xl shadow-xl border overflow-hidden ${
    isDark
      ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
      : `bg-white ${
          currentSkin?.glass ||
          ""
        } border-gray-200`
  }`;

  const neutralButtonClass = `inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition ${
    isDark
      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
      : "border-slate-300 text-slate-700 hover:bg-slate-100"
  }`;

  const tableRowClass = `transition-colors ${
    isDark
      ? "bg-gray-800/20 hover:bg-gray-700/30"
      : "bg-white hover:bg-gray-50"
  }`;

  const tableCellClass = `px-4 py-3 text-sm whitespace-nowrap ${
    isDark
      ? "text-gray-300"
      : "text-gray-900"
  }`;

  /* =======================================================
     STATE
  ======================================================= */

  const [
    showFilters,
    setShowFilters,
  ] = useState(true);

  const [
    loadingDropdowns,
    setLoadingDropdowns,
  ] = useState(false);

  const [
    loadingList,
    setLoadingList,
  ] = useState(false);

  /* =======================================================
     FILTERS
  ======================================================= */

  const [
    fromDate,
    setFromDate,
  ] = useState(
    getDefaultFromDate
  );

  const [
    toDate,
    setToDate,
  ] = useState(
    getTodayDate
  );

  const [
    selectedBranchId,
    setSelectedBranchId,
  ] = useState(() => {
    return storedBranchId
      ? parseInt(
          storedBranchId,
          10
        )
      : 0;
  });

  const [
    vehicleCode,
    setVehicleCode,
  ] = useState("");

  /* =======================================================
     DROPDOWN DATA
  ======================================================= */

  const [
    vehicleOptions,
    setVehicleOptions,
  ] = useState([]);

  const [
    branchList,
    setBranchList,
  ] = useState([]);

  /* =======================================================
     RESULT
  ======================================================= */

  const [
    data,
    setData,
  ] = useState([]);

  /* =======================================================
     PAGINATION
  ======================================================= */

  const [
    currentPage,
    setCurrentPage,
  ] = useState(1);

  const [
    pageSize,
    setPageSize,
  ] = useState(10);

  /* =======================================================
     NORMALIZE DATA

     Preserves both:
     - Stored procedure response
     - Inline SQL response
  ======================================================= */

  const normalizedData =
    useMemo(() => {
      return (
        data || []
      ).map((row) => {
        const preStartHr =
          row.fld_preStartingHr ??
          row.fld_Pre_HSDStartingHr;

        const curStartHr =
          row.fld_curStartingHr ??
          row.fld_Cur_HSDStartingHr;

        const preCloseHr =
          row.fld_preClosingHr ??
          row.fld_Pre_HSDClosingHr;

        const curCloseHr =
          row.fld_curClosingHr ??
          row.fld_Cur_HSDClosingHr;

        const preStartKm =
          row.fld_preStartingKM ??
          row.fld_Pre_HSDStartingKM;

        const curStartKm =
          row.fld_curStartingKM ??
          row.fld_Cur_HSDStartingKM;

        const preCloseKm =
          row.fld_preClosingKM ??
          row.fld_Pre_HSDClosingKM;

        const curCloseKm =
          row.fld_curClosingKM ??
          row.fld_Cur_HSDClosingKM;

        const qty =
          row.fld_Qty ??
          row.fld_qty;

        const vehicleDisplay =
          (row.fld_vehiclecodename &&
          row.fld_vehiclecodename.trim() !==
            ""
            ? row.fld_vehiclecodename
            : null) ||
          (row.fld_vehiclecode &&
          row.fld_vehiclecode.trim() !==
            ""
            ? row.fld_vehiclecode
            : null) ||
          "—";

        return {
          ...row,

          _qty:
            qty,

          _vehicleDisplay:
            vehicleDisplay,

          _preStartHr:
            preStartHr,

          _curStartHr:
            curStartHr,

          _preCloseHr:
            preCloseHr,

          _curCloseHr:
            curCloseHr,

          _preStartKm:
            preStartKm,

          _curStartKm:
            curStartKm,

          _preCloseKm:
            preCloseKm,

          _curCloseKm:
            curCloseKm,

          _sidate:
            row.fld_SIDate ??
            row.fld_siDate ??
            "—",

          _updated:
            row.fld_Date ??
            row.fld_date ??
            "—",

          _user:
            row.fld_username ??
            row.fld_userName ??
            "—",
        };
      });
    }, [data]);

  /* =======================================================
     LOAD DROPDOWNS
  ======================================================= */

  const loadDropdowns =
    useCallback(
      async () => {
        setLoadingDropdowns(
          true
        );

        try {
          const response =
            await axios.get(
              `${BASE}/hsd_dropdown`,
              {
                params: {
                  branchId:
                    selectedBranchId,
                },

                headers:
                  commonHeaders,
              }
            );

          const branches =
            Array.isArray(
              response.data
                ?.branches
            )
              ? response.data
                  .branches
              : [];

          setBranchList(
            branches.filter(
              (item) =>
                item.fld_Branchid !==
                  "" &&
                item.fld_Branchid !=
                  null
            )
          );

          const vehicles =
            Array.isArray(
              response.data
                ?.vehicleCodes
            )
              ? response.data
                  .vehicleCodes
              : [];

          setVehicleOptions(
            vehicles.filter(
              (item) =>
                item.fld_vehichlecode &&
                item.fld_vehichlecode !==
                  "Select"
            )
          );
        } catch (error) {
          console.error(
            "Dropdown fetch failed:",
            error
          );

          toast.error(
            error?.response
              ?.data
              ?.message ||
              "Failed to load dropdown data"
          );

          setBranchList(
            []
          );

          setVehicleOptions(
            []
          );
        } finally {
          setLoadingDropdowns(
            false
          );
        }
      },
      [
        selectedBranchId,
        commonHeaders,
      ]
    );

  /* =======================================================
     DROPDOWN EFFECT
  ======================================================= */

  useEffect(() => {
    loadDropdowns();
  }, [loadDropdowns]);

  /* =======================================================
     SEARCH
  ======================================================= */

  const handleSearch =
    async () => {
      if (
        !fromDate ||
        !toDate
      ) {
        toast.warning(
          "Please Select From Date and To Date"
        );

        return;
      }

      if (
        new Date(
          fromDate
        ).getTime() >
        new Date(
          toDate
        ).getTime()
      ) {
        toast.warning(
          "From Date should not be greater than To Date"
        );

        return;
      }

      if (
        !selectedBranchId
      ) {
        toast.warning(
          "Please select Branch"
        );

        return;
      }

      setLoadingList(
        true
      );

      try {
        const response =
          await axios.get(
            `${BASE}/hsd_list`,
            {
              params: {
                branchId:
                  selectedBranchId ||
                  0,

                fromDate,

                toDate,

                vehicleCode:
                  vehicleCode &&
                  vehicleCode !==
                    ""
                    ? vehicleCode
                    : "%",
              },

              headers:
                commonHeaders,
            }
          );

        const records =
          Array.isArray(
            response.data
              ?.data
          )
            ? response.data
                .data
            : [];

        setData(
          records
        );

        setCurrentPage(
          1
        );

        if (
          records.length ===
          0
        ) {
          toast.info(
            "No records found."
          );
        } else {
          toast.success(
            `Found ${records.length} record(s)`
          );
        }
      } catch (error) {
        console.error(
          "HSD list fetch failed:",
          error
        );

        toast.error(
          error?.response
            ?.data
            ?.message ||
            "Failed to fetch HSD records"
        );

        setData(
          []
        );
      } finally {
        setLoadingList(
          false
        );
      }
    };

  /* =======================================================
     CLEAR FILTERS
  ======================================================= */

  const clearFilters =
    () => {
      setFromDate(
        getDefaultFromDate()
      );

      setToDate(
        getTodayDate()
      );

      setVehicleCode(
        ""
      );

      setSelectedBranchId(
        parseInt(
          storedBranchId,
          10
        ) || 0
      );

      setData([]);

      setCurrentPage(
        1
      );

      toast.info(
        "Filters cleared"
      );
    };

  /* =======================================================
     PAGINATION
  ======================================================= */

  const totalPages =
    Math.max(
      1,
      Math.ceil(
        normalizedData.length /
          pageSize
      )
    );

  const indexOfLastItem =
    currentPage *
    pageSize;

  const indexOfFirstItem =
    indexOfLastItem -
    pageSize;

  const currentItems =
    useMemo(
      () =>
        normalizedData.slice(
          indexOfFirstItem,
          indexOfLastItem
        ),
      [
        normalizedData,
        indexOfFirstItem,
        indexOfLastItem,
      ]
    );

  const paginationPages =
    useMemo(() => {
      if (
        totalPages <= 5
      ) {
        return Array.from(
          {
            length:
              totalPages,
          },
          (_, index) =>
            index + 1
        );
      }

      if (
        currentPage <= 3
      ) {
        return [
          1,
          2,
          3,
          4,
          5,
        ];
      }

      if (
        currentPage >=
        totalPages - 2
      ) {
        return [
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages,
        ];
      }

      return [
        currentPage - 2,
        currentPage - 1,
        currentPage,
        currentPage + 1,
        currentPage + 2,
      ];
    }, [
      currentPage,
      totalPages,
    ]);

  const showingFrom =
    normalizedData.length ===
    0
      ? 0
      : indexOfFirstItem +
        1;

  const showingTo =
    Math.min(
      indexOfLastItem,
      normalizedData.length
    );

  const isLoading =
    loadingDropdowns ||
    loadingList;

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <LayoutWrapper>
      {/* ===================================================
          HEADER
      =================================================== */}

      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            HSD HOUR UPDATION
            LISTING
          </span>
        }
        setSkin={
          setSkin
        }
        currentSkin={
          skin
        }
      />

      <div className="space-y-6 mt-6">
        {/* =================================================
            PAGE HEADER
        ================================================= */}

        <div
          className={
            cardClass
          }
        >
          <div
            className={`px-6 py-4 border-b flex flex-col lg:flex-row lg:items-center justify-between gap-4 ${
              isDark
                ? "border-slate-700/50 bg-slate-900/40"
                : "border-slate-200/50 bg-white/60"
            }`}
          >
            {/* LEFT */}

            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <FaGasPump className="w-5 h-5 text-white" />
              </div>

              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h3
                    className={`text-lg font-bold ${
                      isDark
                        ? "text-white"
                        : "text-slate-900"
                    }`}
                  >
                    HSD Hour
                    Updation
                  </h3>

                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      isDark
                        ? "bg-gray-700 text-gray-300"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {
                      normalizedData.length
                    }{" "}
                    Records
                  </span>

                  {isLoading && (
                    <span
                      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${
                        isDark
                          ? "bg-gray-700 text-gray-300"
                          : `bg-${baseColor}-50 text-${baseColor}-700`
                      }`}
                    >
                      <FaCog className="animate-spin" />

                      Loading...
                    </span>
                  )}
                </div>

                <p
                  className={`text-xs mt-1 ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-600"
                  }`}
                >
                  View HSD starting
                  and closing hour /
                  kilometre updates
                  for vehicles
                </p>
              </div>
            </div>

            {/* RIGHT */}

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() =>
                  setShowFilters(
                    (
                      previous
                    ) =>
                      !previous
                  )
                }
                className={
                  neutralButtonClass
                }
              >
                <FaFilter />

                {showFilters
                  ? "Hide Filters"
                  : "Show Filters"}
              </button>

              <button
                type="button"
                onClick={() =>
                  navigate(-1)
                }
                className={
                  neutralButtonClass
                }
              >
                <FaArrowLeft />

                Back
              </button>

              <button
                type="button"
                onClick={() =>
                  navigate(
                    "/inventory/VehicleHsdHourUpdation"
                  )
                }
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700`}
              >
                <FaPlus />

                Add New
              </button>
            </div>
          </div>
        </div>

        {/* =================================================
            FILTER CARD
        ================================================= */}

        {showFilters && (
          <div
            className={
              cardClass
            }
          >
            {/* HEADER */}

            <div
              className={`px-6 py-4 border-b ${
                isDark
                  ? "border-slate-700/50 bg-slate-900/40"
                  : "border-slate-200/50 bg-white/60"
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
                >
                  <FaFilter className="w-4 h-4 text-white" />
                </div>

                <div>
                  <h3
                    className={`text-lg font-bold ${
                      isDark
                        ? "text-white"
                        : "text-slate-900"
                    }`}
                  >
                    Filtration Area
                  </h3>

                  <p
                    className={`text-xs ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-600"
                    }`}
                  >
                    Filter HSD hour
                    updates by date,
                    branch and vehicle
                  </p>
                </div>
              </div>
            </div>

            {/* BODY */}

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                {/* FROM DATE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    From Date

                    <span className="text-red-500 ml-1">
                      *
                    </span>
                  </label>

                  <input
                    type="date"
                    value={
                      fromDate
                    }
                    onChange={(
                      event
                    ) =>
                      setFromDate(
                        event.target
                          .value
                      )
                    }
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* TO DATE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    To Date

                    <span className="text-red-500 ml-1">
                      *
                    </span>
                  </label>

                  <input
                    type="date"
                    value={
                      toDate
                    }
                    onChange={(
                      event
                    ) =>
                      setToDate(
                        event.target
                          .value
                      )
                    }
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* BRANCH */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Branch
                  </label>

                  <select
                    value={
                      selectedBranchId
                    }
                    onChange={(
                      event
                    ) => {
                      const value =
                        event.target
                          .value;

                      setSelectedBranchId(
                        value ===
                          "" ||
                        value ===
                          "0"
                          ? 0
                          : parseInt(
                              value,
                              10
                            )
                      );

                      setVehicleCode(
                        ""
                      );

                      setData([]);

                      setCurrentPage(
                        1
                      );
                    }}
                    disabled={
                      loadingDropdowns
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="0">
                      Select Branch
                    </option>

                    {branchList.map(
                      (
                        branchItem,
                        index
                      ) => (
                        <option
                          key={
                            branchItem.fld_Branchid ||
                            index
                          }
                          value={String(
                            branchItem.fld_Branchid
                          )}
                        >
                          {
                            branchItem.fld_BranchName
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* VEHICLE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Vehicle Code
                  </label>

                  <select
                    value={
                      vehicleCode
                    }
                    onChange={(
                      event
                    ) =>
                      setVehicleCode(
                        event.target
                          .value
                      )
                    }
                    disabled={
                      !selectedBranchId ||
                      selectedBranchId ===
                        0 ||
                      loadingDropdowns
                    }
                    className={`${inputClass} ${
                      !selectedBranchId ||
                      selectedBranchId ===
                        0
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    <option value="">
                      Select Vehicle
                    </option>

                    {vehicleOptions.map(
                      (
                        vehicle,
                        index
                      ) => (
                        <option
                          key={`${vehicle.fld_vehichlecode}-${index}`}
                          value={
                            vehicle.fld_vehichlecode
                          }
                        >
                          {
                            vehicle.fld_vehichlecode
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* PAGE SIZE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Page Size
                  </label>

                  <select
                    value={
                      pageSize
                    }
                    onChange={(
                      event
                    ) => {
                      setPageSize(
                        Number(
                          event.target
                            .value
                        ) ||
                          10
                      );

                      setCurrentPage(
                        1
                      );
                    }}
                    className={
                      inputClass
                    }
                  >
                    {[
                      10,
                      25,
                      50,
                      100,
                    ].map(
                      (
                        size
                      ) => (
                        <option
                          key={
                            size
                          }
                          value={
                            size
                          }
                        >
                          {
                            size
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>

              {/* ACTIONS */}

              <div
                className={`flex flex-col md:flex-row md:items-center justify-between gap-4 mt-6 pt-5 border-t ${
                  isDark
                    ? "border-slate-700"
                    : "border-slate-200"
                }`}
              >
                <button
                  type="button"
                  onClick={
                    clearFilters
                  }
                  className={
                    neutralButtonClass
                  }
                >
                  <FaTimes />

                  Clear Filters
                </button>

                <button
                  type="button"
                  onClick={
                    handleSearch
                  }
                  disabled={
                    loadingList
                  }
                  className={`inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-60 disabled:cursor-not-allowed`}
                >
                  {loadingList ? (
                    <FaCog className="animate-spin" />
                  ) : (
                    <FaSearch />
                  )}

                  {loadingList
                    ? "Searching..."
                    : "Search Records"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* =================================================
            RESULT CARD
        ================================================= */}

        <div
          className={
            cardClass
          }
        >
          {/* HEADER */}

          <div
            className={`p-6 border-b ${
              isDark
                ? "border-gray-700"
                : "border-gray-200"
            }`}
          >
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <div>
                  <h3
                    className={`text-xl font-bold ${
                      isDark
                        ? "text-white"
                        : "text-gray-900"
                    }`}
                  >
                    HSD Hour
                    Updation Records
                  </h3>

                  <p
                    className={`text-xs mt-1 ${
                      isDark
                        ? "text-gray-400"
                        : "text-gray-500"
                    }`}
                  >
                    Previous and
                    current HSD hour /
                    kilometre values
                  </p>
                </div>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {
                    normalizedData.length
                  }{" "}
                  records
                </span>
              </div>

              {normalizedData.length >
                0 && (
                <span
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                    isDark
                      ? `bg-${baseColor}-900/30 text-${baseColor}-300`
                      : `bg-${baseColor}-50 text-${baseColor}-700`
                  }`}
                >
                  Page{" "}
                  {
                    currentPage
                  }{" "}
                  /{" "}
                  {
                    totalPages
                  }
                </span>
              )}
            </div>
          </div>

          {/* =================================================
              LOADING
          ================================================= */}

          {loadingList ? (
            <div className="text-center py-16">
              <FaCog
                className={`w-10 h-10 mx-auto animate-spin text-${baseColor}-500`}
              />

              <p
                className={`mt-3 text-sm ${
                  isDark
                    ? "text-gray-400"
                    : "text-gray-500"
                }`}
              >
                Loading HSD Hour
                records...
              </p>
            </div>
          ) : normalizedData.length >
            0 ? (
            <>
              {/* =============================================
                  TABLE
              ============================================= */}

              <div className="overflow-x-auto">
                <table className="min-w-[2200px] w-full text-sm">
                  <thead
                    className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                  >
                    <tr>
                      <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                        S.No
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                        SI Date
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                        Updated Date
                      </th>

                      <th className="px-4 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">
                        Qty
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                        Vehicle Code :
                        Name
                      </th>

                      <th className="px-4 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">
                        Pre Starting Hr
                      </th>

                      <th className="px-4 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">
                        Cur Starting Hr
                      </th>

                      <th className="px-4 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">
                        Pre Closing Hr
                      </th>

                      <th className="px-4 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">
                        Cur Closing Hr
                      </th>

                      <th className="px-4 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">
                        Pre Starting KM
                      </th>

                      <th className="px-4 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">
                        Cur Starting KM
                      </th>

                      <th className="px-4 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">
                        Pre Closing KM
                      </th>

                      <th className="px-4 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">
                        Cur Closing KM
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                        User Name
                      </th>
                    </tr>
                  </thead>

                  <tbody
                    className={`divide-y ${
                      isDark
                        ? "divide-gray-700"
                        : "divide-gray-200"
                    }`}
                  >
                    {currentItems.map(
                      (
                        item,
                        index
                      ) => (
                        <tr
                          key={
                            item.fld_HSDHrUpdationId ??
                            `${item._vehicleDisplay}-${index}`
                          }
                          className={
                            tableRowClass
                          }
                        >
                          {/* S.NO */}

                          <td
                            className={`${tableCellClass} text-center font-medium`}
                          >
                            {indexOfFirstItem +
                              index +
                              1}
                          </td>

                          {/* SI DATE */}

                          <td
                            className={
                              tableCellClass
                            }
                          >
                            <div className="flex items-center gap-2">
                              <FaClock
                                className={`text-${baseColor}-500`}
                              />

                              <span>
                                {showVal(
                                  item._sidate
                                )}
                              </span>
                            </div>
                          </td>

                          {/* UPDATED */}

                          <td
                            className={
                              tableCellClass
                            }
                          >
                            {showVal(
                              item._updated
                            )}
                          </td>

                          {/* QTY */}

                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <span
                              className={`inline-flex min-w-[65px] justify-end px-3 py-1.5 rounded-lg font-semibold ${
                                isDark
                                  ? "bg-gray-700/60 text-gray-200"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {showVal(
                                item._qty
                              )}
                            </span>
                          </td>

                          {/* VEHICLE */}

                          <td
                            className={
                              tableCellClass
                            }
                          >
                            <div className="flex items-center gap-2">
                              <FaCar
                                className={`text-${baseColor}-500 flex-shrink-0`}
                              />

                              <span className="font-semibold">
                                {showVal(
                                  item._vehicleDisplay
                                )}
                              </span>
                            </div>
                          </td>

                          {/* PRE START HR */}

                          <td
                            className={`${tableCellClass} text-right`}
                          >
                            {showVal(
                              item._preStartHr
                            )}
                          </td>

                          {/* CUR START HR */}

                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <span
                              className={`inline-flex min-w-[80px] justify-end px-3 py-1 rounded-lg font-semibold ${
                                isDark
                                  ? `bg-${baseColor}-900/30 text-${baseColor}-300`
                                  : `bg-${baseColor}-50 text-${baseColor}-700`
                              }`}
                            >
                              {showVal(
                                item._curStartHr
                              )}
                            </span>
                          </td>

                          {/* PRE CLOSE HR */}

                          <td
                            className={`${tableCellClass} text-right`}
                          >
                            {showVal(
                              item._preCloseHr
                            )}
                          </td>

                          {/* CUR CLOSE HR */}

                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <span
                              className={`inline-flex min-w-[80px] justify-end px-3 py-1 rounded-lg font-semibold ${
                                isDark
                                  ? "bg-emerald-900/30 text-emerald-300"
                                  : "bg-emerald-50 text-emerald-700"
                              }`}
                            >
                              {showVal(
                                item._curCloseHr
                              )}
                            </span>
                          </td>

                          {/* PRE START KM */}

                          <td
                            className={`${tableCellClass} text-right`}
                          >
                            {showVal(
                              item._preStartKm
                            )}
                          </td>

                          {/* CUR START KM */}

                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <span
                              className={`inline-flex min-w-[80px] justify-end px-3 py-1 rounded-lg font-semibold ${
                                isDark
                                  ? `bg-${baseColor}-900/30 text-${baseColor}-300`
                                  : `bg-${baseColor}-50 text-${baseColor}-700`
                              }`}
                            >
                              {showVal(
                                item._curStartKm
                              )}
                            </span>
                          </td>

                          {/* PRE CLOSE KM */}

                          <td
                            className={`${tableCellClass} text-right`}
                          >
                            {showVal(
                              item._preCloseKm
                            )}
                          </td>

                          {/* CUR CLOSE KM */}

                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <span
                              className={`inline-flex min-w-[80px] justify-end px-3 py-1 rounded-lg font-semibold ${
                                isDark
                                  ? "bg-emerald-900/30 text-emerald-300"
                                  : "bg-emerald-50 text-emerald-700"
                              }`}
                            >
                              {showVal(
                                item._curCloseKm
                              )}
                            </span>
                          </td>

                          {/* USER */}

                          <td
                            className={
                              tableCellClass
                            }
                          >
                            {showVal(
                              item._user
                            )}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>

              {/* =============================================
                  PAGINATION
              ============================================= */}

              <div
                className={`p-6 border-t ${
                  isDark
                    ? "border-gray-700"
                    : "border-gray-200"
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* INFO */}

                  <div
                    className={`text-sm ${
                      isDark
                        ? "text-gray-300"
                        : "text-gray-700"
                    }`}
                  >
                    Showing{" "}
                    {
                      showingFrom
                    }{" "}
                    to{" "}
                    {
                      showingTo
                    }{" "}
                    of{" "}
                    {
                      normalizedData.length
                    }{" "}
                    results
                  </div>

                  {/* CONTROLS */}

                  <div
                    className={`inline-flex rounded-lg shadow-sm overflow-hidden border ${
                      isDark
                        ? "border-gray-700 bg-gray-900"
                        : "border-gray-300 bg-white"
                    }`}
                  >
                    {/* PREVIOUS */}

                    <button
                      type="button"
                      onClick={() =>
                        setCurrentPage(
                          (
                            previous
                          ) =>
                            Math.max(
                              1,
                              previous -
                                1
                            )
                        )
                      }
                      disabled={
                        currentPage ===
                        1
                      }
                      className={`px-4 py-2 text-sm font-medium transition ${
                        currentPage ===
                        1
                          ? "text-gray-400 cursor-not-allowed"
                          : isDark
                          ? "text-gray-200 hover:bg-gray-800"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      Previous
                    </button>

                    {/* PAGE NUMBERS */}

                    {paginationPages.map(
                      (
                        pageNumber
                      ) => (
                        <button
                          type="button"
                          key={
                            pageNumber
                          }
                          onClick={() =>
                            setCurrentPage(
                              pageNumber
                            )
                          }
                          className={`px-4 py-2 text-sm font-medium transition ${
                            currentPage ===
                            pageNumber
                              ? `bg-${baseColor}-${baseShade} text-white`
                              : isDark
                              ? "text-gray-200 hover:bg-gray-800"
                              : "text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          {
                            pageNumber
                          }
                        </button>
                      )
                    )}

                    {/* NEXT */}

                    <button
                      type="button"
                      onClick={() =>
                        setCurrentPage(
                          (
                            previous
                          ) =>
                            Math.min(
                              totalPages,
                              previous +
                                1
                            )
                        )
                      }
                      disabled={
                        currentPage ===
                        totalPages
                      }
                      className={`px-4 py-2 text-sm font-medium transition ${
                        currentPage ===
                        totalPages
                          ? "text-gray-400 cursor-not-allowed"
                          : isDark
                          ? "text-gray-200 hover:bg-gray-800"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* ===============================================
                EMPTY
            =============================================== */

            <div className="text-center py-14">
              <div
                className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                  isDark
                    ? "bg-gray-800"
                    : "bg-gray-100"
                }`}
              >
                <FaInfoCircle className="w-8 h-8 text-gray-400" />
              </div>

              <h3
                className={`font-semibold ${
                  isDark
                    ? "text-gray-200"
                    : "text-gray-800"
                }`}
              >
                No HSD Hour Records
                Found
              </h3>

              <p
                className={`text-sm mt-1 ${
                  isDark
                    ? "text-gray-400"
                    : "text-gray-500"
                }`}
              >
                Select the required
                filters and click
                Search Records.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ===================================================
          TOAST
      =================================================== */}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={
          isDark
            ? "dark"
            : "light"
        }
      />
    </LayoutWrapper>
  );
};

export default HsdHrUpdate;